// ─────────────────────────────────────────────────────────────────────────────
// QA (2026-08-27) — al cerrar sesión, un usuario distinto que entre después en el MISMO navegador
// no debe heredar información del anterior. `LogoutButton` ya limpiaba la sesión propiamente dicha
// (token/usuario Frontline, o el caché de MSAL vía `logoutRedirect`); esto cubre lo que quedaba
// fuera: claves de `localStorage` que no son parte de ninguna sesión, pero SÍ pueden hacer que la
// app se vea/comporte como si siguiera siendo la persona anterior.
//
// Qué se limpia y por qué (ver docs/contexto/errores-conocidos.md para el análisis completo de cada
// clave de localStorage del repo):
//   · sigma-demo-user / sigma-demo-slots / sigma-demo-active-slot — identidad SIMULADA del rol
//     simulator (UserConfigFAB). No es información real de nadie, pero si sobrevive, la siguiente
//     persona que entra ve la app como si YA estuviera simulando el rol que dejó la anterior.
//   · wrappedReadySeen{año} — no está namespaced por usuario (a diferencia de
//     pmp.streak.badgesSeen.{userId}, que sí lo está y por eso NO se toca aquí): si la persona A ya
//     vio su aviso de Wrapped, la persona B que entre después nunca lo vería, aunque sea la primera
//     vez que lo tiene disponible.
//   · ri_guide_seen — mismo problema: es global al navegador, no por persona.
//
// Qué NO se toca a propósito (perder esto SÍ sería una regresión):
//   · sigma-theme — preferencia genuina que conviene conservar entre sesiones.
//   · pmp.streak.badgesSeen.{userId} — ya namespaced por usuario; limpiarlo repetiría animaciones
//     de celebración que esa misma persona ya vio.
//   · pmp_score_bands / pmp_behavior_levels / sigma_icon_catalog — configuración de admin SIN
//     respaldo en backend (verificado: ninguno de esos 3 archivos hace ninguna llamada a API).
//     localStorage es la ÚNICA fuente de esos datos; borrarlos sería pérdida real de configuración,
//     no solo "arreglar" quién ve qué.
//   · Preferencias de layout puramente cosméticas (sidebar/admin-flyout colapsados, pestaña de admin
//     activa) — no son "información" de nadie, y conservarlas entre sesiones es la UX esperada.
// ─────────────────────────────────────────────────────────────────────────────

const EXACT_KEYS_TO_CLEAR = [
  'sigma-demo-user',
  'sigma-demo-slots',
  'sigma-demo-active-slot',
  'ri_guide_seen',
];

/** Prefijos a limpiar completos — hay una clave por año (`wrappedReadySeen2026`,
 *  `wrappedReadySeen2027`, ...), así que se listan por prefijo, no por valor exacto. */
const KEY_PREFIXES_TO_CLEAR = ['wrappedReadySeen'];

/**
 * Limpia del `localStorage` lo que podría hacer que un usuario nuevo, en el mismo navegador, vea
 * información o comportamiento que en realidad pertenece al usuario que acaba de cerrar sesión.
 *
 * Deliberadamente NO limpia el token/objeto de sesión (Frontline o MSAL) — de eso ya se encarga
 * `LogoutButton` justo antes de llamar a esta función, cada uno con su propio mecanismo.
 */
export function clearUserCache(): void {
  try {
    EXACT_KEYS_TO_CLEAR.forEach((key) => localStorage.removeItem(key));

    // No se puede listar por prefijo directo en localStorage: hay que recorrer sus claves. Se copian
    // primero a un array — mutar localStorage mientras se itera `localStorage.key(i)` desordena los
    // índices y salta claves.
    const allKeys = Object.keys(localStorage);
    allKeys
      .filter((key) => KEY_PREFIXES_TO_CLEAR.some((prefix) => key.startsWith(prefix)))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage no disponible (modo privado): no hay nada que limpiar */
  }
}
